import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, orderBy, doc, updateDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { ProposalDownload } from '../types';
import { AppHeader } from '../components/ui/AppHeader';
import { ArrowLeft, Search, Download, FileText, Archive, Trash2, CheckSquare, Square } from 'lucide-react';
import './ProposalDownloadHistoryPage.css';

const ProposalDownloadHistoryPage: React.FC = () => {
    const navigate = useNavigate();
    const [downloads, setDownloads] = useState<(ProposalDownload & { id: string })[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [archiving, setArchiving] = useState(false);

    useEffect(() => {
        const fetchDownloads = async () => {
            try {
                const downloadsRef = collection(db, 'proposalDownloads');
                const q = query(downloadsRef, orderBy('downloadedAt', 'desc'));
                const snapshot = await getDocs(q);

                const downloadData = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                })) as (ProposalDownload & { id: string })[];

                // Filter out archived downloads
                setDownloads(downloadData.filter(d => !(d as any).archived));
            } catch (error) {
                console.error('Error fetching downloads:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchDownloads();
    }, []);

    const filteredDownloads = useMemo(() => {
        if (!searchQuery.trim()) return downloads;

        const q = searchQuery.toLowerCase();
        return downloads.filter(d =>
            d.clientName?.toLowerCase().includes(q) ||
            d.proposalId?.toLowerCase().includes(q) ||
            d.dealId?.toLowerCase().includes(q)
        );
    }, [downloads, searchQuery]);

    const formatDate = (date: any) => {
        if (!date) return '—';
        try {
            const d = date && typeof date === 'object' && 'toDate' in date
                ? date.toDate()
                : date && typeof date.seconds === 'number'
                    ? new Date(date.seconds * 1000)
                    : new Date(date);
            if (isNaN(d.getTime())) return '—';
            return d.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit'
            });
        } catch {
            return '—';
        }
    };

    const formatCurrency = (amount?: number) => {
        if (!amount) return '—';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 0,
        }).format(amount);
    };

    const toggleSelect = (id: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (selectedIds.size === filteredDownloads.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(filteredDownloads.map(d => d.id)));
        }
    };

    const handleArchiveSelected = async () => {
        if (selectedIds.size === 0) return;

        const confirmed = window.confirm(
            `Archive ${selectedIds.size} download record(s)? They will be hidden from this list.`
        );
        if (!confirmed) return;

        setArchiving(true);
        try {
            const archivePromises = Array.from(selectedIds).map(id =>
                updateDoc(doc(db, 'proposalDownloads', id), { archived: true })
            );
            await Promise.all(archivePromises);

            // Remove from local state (they're now archived)
            setDownloads(prev => prev.filter(d => !selectedIds.has(d.id)));
            setSelectedIds(new Set());
        } catch (error) {
            console.error('Error archiving downloads:', error);
            alert('Failed to archive some records. Please try again.');
        } finally {
            setArchiving(false);
        }
    };

    const allSelected = filteredDownloads.length > 0 && selectedIds.size === filteredDownloads.length;
    const someSelected = selectedIds.size > 0;

    return (
        <div className="download-history-page">
            <AppHeader />
            <main className="download-history-main">
                <div className="download-history-header">
                    <button className="back-btn" onClick={() => navigate('/proposals')} title="Back to Proposals">
                        <ArrowLeft size={18} />
                    </button>
                    <h1>Download History</h1>
                    <p className="subtitle">{downloads.length} downloads recorded</p>
                </div>

                <div className="toolbar">
                    <div className="download-search">
                        <Search size={16} />
                        <input
                            type="text"
                            placeholder="Search by client name..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>

                    {someSelected && (
                        <div className="bulk-actions">
                            <span className="selection-count">{selectedIds.size} selected</span>
                            <button
                                className="archive-btn"
                                onClick={handleArchiveSelected}
                                disabled={archiving}
                            >
                                <Archive size={16} />
                                {archiving ? 'Archiving...' : 'Archive Selected'}
                            </button>
                        </div>
                    )}
                </div>

                {loading ? (
                    <div className="loading-state">
                        <p>Loading downloads...</p>
                    </div>
                ) : filteredDownloads.length === 0 ? (
                    <div className="empty-state">
                        <FileText size={48} />
                        <p>{searchQuery ? 'No downloads match your search' : 'No downloads recorded yet'}</p>
                    </div>
                ) : (
                    <div className="downloads-table">
                        <table>
                            <thead>
                                <tr>
                                    <th className="col-checkbox">
                                        <button
                                            className="select-all-btn"
                                            onClick={toggleSelectAll}
                                            title={allSelected ? 'Deselect all' : 'Select all'}
                                        >
                                            {allSelected ? <CheckSquare size={16} /> : <Square size={16} />}
                                        </button>
                                    </th>
                                    <th className="col-client">Client</th>
                                    <th className="col-pdf">PDF</th>
                                    <th className="col-date">Downloaded</th>
                                    <th className="col-amount">Amount</th>
                                    <th className="col-type">Type</th>
                                    <th className="col-by">By</th>
                                    <th className="col-signed">Signed</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredDownloads.map(download => (
                                    <tr
                                        key={download.id}
                                        className={selectedIds.has(download.id) ? 'selected' : ''}
                                    >
                                        <td className="col-checkbox">
                                            <button
                                                className="row-checkbox"
                                                onClick={() => toggleSelect(download.id)}
                                            >
                                                {selectedIds.has(download.id)
                                                    ? <CheckSquare size={16} />
                                                    : <Square size={16} />
                                                }
                                            </button>
                                        </td>
                                        <td>
                                            <span className="client-name">{download.clientName || '—'}</span>
                                        </td>
                                        <td className="col-pdf">
                                            {download.pdfStorageUrl ? (
                                                <a
                                                    href={download.pdfStorageUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="download-btn"
                                                    title="Download PDF"
                                                >
                                                    <Download size={14} />
                                                </a>
                                            ) : (
                                                <span className="no-pdf">—</span>
                                            )}
                                        </td>
                                        <td>
                                            <span className="date">{formatDate(download.downloadedAt)}</span>
                                        </td>
                                        <td>
                                            <span className="amount">{formatCurrency(download.totalAmount)}</span>
                                        </td>
                                        <td>
                                            <span className={`badge ${download.isKitProposal ? 'kit' : 'custom'}`}>
                                                {download.isKitProposal ? 'Kit' : 'Custom'}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`badge ${download.downloadedBy || 'client'}`}>
                                                {download.downloadedBy === 'employee' ? 'Employee'
                                                    : download.downloadedBy === 'admin' ? 'Admin'
                                                        : 'Client'}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`badge ${download.isSigned ? 'yes' : 'no'}`}>
                                                {download.isSigned ? 'Yes' : 'No'}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </main>
        </div>
    );
};

export default ProposalDownloadHistoryPage;

